<template>
  <AIAssistantBaseChatResponse
    v-if="stream"
    :loading="aiStreamSummary.loading"
  >
    <div class="tw:grid tw:gap-4">
      <AIAssistantBaseChatType
        html
        @complete="updateChatState"
      >
        <i18n-t
          v-if="chatState === 'loading'"
          keypath="ai_summary_panel.summary_loading"
          scope="global"
        >
          <template #label>
            <span class="tw:font-bold">{{ stream.label }}</span>
          </template>
        </i18n-t>

        <i18n-t
          v-else-if="chatState === 'complete'"
          keypath="ai_summary_panel.summary_complete"
          scope="global"
        >
          <template #label>
            <span class="tw:font-bold">{{ stream.label }}</span>
          </template>
        </i18n-t>
      </AIAssistantBaseChatType>

      <AIAssistantBaseChatTransition
        v-if="showResult"
        appear
        class="tw:grid tw:gap-4 tw:leading-tight"
      >
        <div
          class="tw:flex"
          data-index="0"
        >
          <InputDatePicker
            bordered
            circle
            close-on-update
            data-testid="date-picker"
            date-style="medium"
            flat
            :label="datePickerLabel"
            limit-to-past-year
            :range="aiStreamSummary.dateRange"
            :range-options="rangeOptions"
            range-selection="custom"
            :range-span-limit="30"
            show-date-input
            show-range-options
            show-time-input
            show-update-button
            @update:range="updateRange"
          />
        </div>

        <AIAssistantBaseChatTransition
          v-if="aiStreamSummary.result"
          appear
          class="tw:grid tw:gap-4"
          data-index="1"
          :delay="300"
          group
        >
          <AISummaryStreamCluster
            v-for="(summaryTopic, summaryTopicIndex) in aiStreamSummary.result
              .topics"
            :key="summaryTopicIndex"
            :assistant="assistant"
            :data-index="summaryTopicIndex"
            :stream-id="streamId"
            :topic-id="summaryTopic.id"
          />
        </AIAssistantBaseChatTransition>

        <AIAssistantBaseChatTransition
          v-else-if="aiStreamSummary.error"
          appear
          data-index="1"
          :delay="300"
          group
        >
          <div
            v-if="aiStreamSummary.status === 422"
            class="tw:flex tw:gap-2"
          >
            <AIIcon class="tw:shrink-0" />
            <div class="tw:ml-1">
              {{ getLocaleText("ai_summary.not_enough_mentions") }}
            </div>
          </div>
          <div
            v-else
            class="tw:flex tw:gap-2"
          >
            <BaseIcon
              class="tw:text-lg tw:text-orange-500"
              icon="exclamation-circle"
            />
            <div class="tw:ml-1 tw:text-orange-500">
              {{ getLocaleText("ai_summary.summary_error") }}
            </div>
          </div>
        </AIAssistantBaseChatTransition>
      </AIAssistantBaseChatTransition>
    </div>
  </AIAssistantBaseChatResponse>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatResponse from "shared/components/AI/AIAssistant/AIAssistantBaseChatResponse.vue";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AIIcon from "shared/components/AI/AIIcon.vue";
import { BaseIcon, InputDatePicker } from "shared/components/base";
import DateRange from "shared/helpers/DateRange";
import useAIStore, { type AISummary } from "shared/stores/ai";
import useStreamsStore from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import { type Nullable, type Stream } from "shared/types";

import AISummaryStreamCluster from "./AISummaryStreamCluster.vue";

interface AISummaryStreamResponseProps {
  streamId: number;
  load?: boolean;
  assistant?: boolean;
}

const props = defineProps<AISummaryStreamResponseProps>();

const aiStore = useAIStore();
const { loadAISummary, getAISummary, setAISummary } = aiStore;

const streamsStore = useStreamsStore();
const { getStreamById } = streamsStore;

const userStore = useUserStore();
const { betaEnabled } = storeToRefs(userStore);

const stream = ref<Nullable<Stream>>(null);
const showResult = ref<boolean>(false);
const chatState = ref<string>("loading");

const aiStreamSummary = computed<Required<AISummary>>(() =>
  getAISummary(props.streamId)
);

const datePickerLabel = computed<string>(() =>
  aiStreamSummary.value.dateRangeUpdated
    ? ""
    : aiStreamSummary.value.dateRange.prettyRangeText
);

const rangeOptions = computed<DateRange[]>(() => [
  DateRange.today(),
  DateRange.yesterday(),
  DateRange.lastThreeDays(),
  DateRange.lastSevenDays(),
  DateRange.lastFourteenDays(),
  DateRange.lastThirtyDays(),
]);

function updateChatState() {
  if (chatState.value === "loading") {
    loadAISummary(props.streamId, betaEnabled.value);
  } else if (chatState.value === "complete") {
    showResult.value = true;
  }
}

function updateRange(range: Nullable<DateRange>) {
  if (!range) return;

  setAISummary(props.streamId, {
    dateRange: range,
    dateRangeUpdated: true,
    result: null,
    error: false,
  });

  showResult.value = false;
  chatState.value = "loading";
}

watch(
  () => props.streamId,
  () => {
    showResult.value = false;

    const streamFromStore = getStreamById(props.streamId);

    if (!streamFromStore) {
      return;
    }

    stream.value = streamFromStore;

    if (props.load || aiStreamSummary.value.result === null) {
      chatState.value = "loading";
    } else {
      chatState.value = "complete";
    }
  },
  { immediate: true }
);

watch(
  () => aiStreamSummary.value,
  () => {
    if (aiStreamSummary.value.result || aiStreamSummary.value.error) {
      chatState.value = "complete";
    }
  },
  { deep: true }
);
</script>
